"use client"

import { useEffect, useState } from "react"
import { UserRoundPlus } from "lucide-react"

import { AddToCalendar } from "@/components/marketing/add-to-calendar"
import { marketingActionClass } from "@/components/marketing/marketing-action"

/**
 * The pair on the dark closing band of the page after booking: Add to
 * calendar, and beside it Save our number, then the one line saying why.
 *
 * The coach rings a booked man from the business line, and a phone that
 * silences unknown callers never rings for a number it has not seen. So the
 * button hands him the very contact card the booking email attaches
 * (`/api/contact-card`, built from the same builder), and it appears only for
 * a booking that really is a phone call from that line: the same one-boolean
 * answer the email's own card uses (`bookingGetsContactCard`), read off the
 * lookup Add to calendar already makes. A man whose call comes another way (an
 * international number is called on WhatsApp, a paid coaching call is on
 * Google Meet) is never told to save a number that will not call him.
 *
 * It shows nothing new while the answer is unknown or the lookup fails: the
 * band then looks exactly as it did before the button existed.
 */
export function CallCloseActions({
  uid,
  fallbackStart,
  fallbackEnd,
  assumePhoneCall = false,
  saveLabel,
  reason,
}: {
  uid: string | null
  fallbackStart: string | null
  fallbackEnd: string | null
  /** The workbench cell's representative booking has no uid to look up. It
   *  stands for an ordinary application call from the business line, so the
   *  cell shows the pair; the live page never sets this. */
  assumePhoneCall?: boolean
  /** The two public strings come from the module so the landing-copy check,
   *  which scans web/modules, fingerprints them. */
  saveLabel: string
  reason: string
}) {
  const showSave = useBookingGetsContactCard(uid, assumePhoneCall)
  return (
    <CallCloseLayout showSave={showSave} saveLabel={saveLabel} reason={reason}>
      <AddToCalendar uid={uid} fallbackStart={fallbackStart} fallbackEnd={fallbackEnd} />
    </CallCloseLayout>
  )
}

/**
 * Whether this booking is a phone call from the business line, the one answer
 * every Save our number surface waits for. With no uid there is nothing to
 * look up and `assumePhoneCall` stands in (the workbench cell). With a uid it
 * stays false until the lookup says so, so a surface shows nothing new while
 * the answer is unknown or the lookup fails.
 */
export function useBookingGetsContactCard(uid: string | null, assumePhoneCall = false): boolean {
  // The uid that was told yes, so a different booking drops the previous answer.
  const [yesFor, setYesFor] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    // The booking row is written by the Cal webhook, and this page opens the
    // instant Cal reports the booking, so the first lookup can come back 404
    // before the row lands. Ask again a few times, then give up quietly.
    const retryDelaysMs = [2000, 5000, 10000]
    const lookup = (attempt: number) => {
      fetch(`/api/booking/calendar?uid=${encodeURIComponent(uid)}`)
        .then(async (r) => {
          if (cancelled) return
          if (r.status === 404 && attempt < retryDelaysMs.length) {
            timer = setTimeout(() => lookup(attempt + 1), retryDelaysMs[attempt])
            return
          }
          const body = r.ok ? await r.json() : null
          if (!cancelled && body?.ok && body.event?.getsContactCard === true) setYesFor(uid)
        })
        .catch(() => {
          /* unknown or failed: show nothing new */
        })
    }
    lookup(0)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [uid])

  return uid ? yesFor === uid : assumePhoneCall
}

function CallCloseLayout({
  showSave,
  saveLabel,
  reason,
  children,
}: {
  showSave: boolean
  saveLabel: string
  reason: string
  children: React.ReactNode
}) {
  // Without the button the calendar control stands alone exactly as before.
  if (!showSave) return <>{children}</>
  return (
    <>
      {/* Side by side, wrapping when the row runs out; under 440px they stack
          at one width. The calendar control's own button is stretched to the
          column so the two match, without touching the shared control. From
          440px up, the open menu (15rem, wider than its button) gets -2rem
          side margins so opening it moves neither button. */}
      <div className="flex flex-wrap items-start justify-center gap-2.5 min-[440px]:[&>div>div]:-mx-8 max-[440px]:w-full max-[440px]:max-w-[240px] max-[440px]:flex-col max-[440px]:items-stretch max-[440px]:[&>div>button]:w-full">
        {children}
        <a
          href="/api/contact-card"
          download="cold-approach-contact.vcf"
          className={marketingActionClass({
            variant: "secondary",
            size: "md",
            className: "border-white/[0.34] text-white hover:border-white hover:text-white",
          })}
        >
          <UserRoundPlus size={16} aria-hidden />
          {saveLabel}
        </a>
      </div>
      {/* Set inline: the closing band's own stylesheet fixes every paragraph at
          15px with a 24px foot, and a utility class cannot beat it. */}
      <p style={{ margin: 0, fontSize: "0.8125rem", lineHeight: 1.5 }}>{reason}</p>
    </>
  )
}
